/**
 * ScavengerHunt - Gamified engagement feature for events
 * Attendees scan QR codes at locations to earn points and compete on leaderboard
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Trophy, MapPin, QrCode, Plus, Star, Users, Target,
  Loader2, Edit2, Trash2, Gift, Medal, Crown, Sparkles
} from 'lucide-react';
import api from '@/lib/api';

const ScavengerHunt = ({ eventId, isAdmin = false }) => {
  const queryClient = useQueryClient();
  const [isAddingChallenge, setIsAddingChallenge] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    location: '',
    points: 10,
    hint: ''
  });

  // Fetch challenges
  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ['scavenger-challenges', eventId],
    queryFn: () => api.get(`/events/${eventId}/scavenger/challenges`).then(r => r.data),
  });

  // Fetch leaderboard
  const { data: leaderboard = [] } = useQuery({
    queryKey: ['scavenger-leaderboard', eventId],
    queryFn: () => api.get(`/events/${eventId}/scavenger/leaderboard`).then(r => r.data),
  });

  // Fetch user progress (for delegates)
  const { data: myProgress } = useQuery({
    queryKey: ['scavenger-progress', eventId],
    queryFn: () => api.get(`/events/${eventId}/scavenger/my-progress`).then(r => r.data),
    enabled: !isAdmin
  });

  // Create challenge mutation (admin)
  const createChallengeMutation = useMutation({
    mutationFn: (challenge) => api.post(`/admin/events/${eventId}/scavenger/challenges`, challenge),
    onSuccess: () => {
      toast.success('Challenge created!');
      queryClient.invalidateQueries(['scavenger-challenges', eventId]);
      setIsAddingChallenge(false);
      setNewChallenge({ title: '', description: '', location: '', points: 10, hint: '' });
    },
    onError: () => toast.error('Failed to create challenge')
  });

  // Update challenge mutation
  const updateChallengeMutation = useMutation({
    mutationFn: ({ challengeId, updates }) => 
      api.put(`/admin/events/${eventId}/scavenger/challenges/${challengeId}`, updates),
    onSuccess: () => {
      toast.success('Challenge updated!');
      queryClient.invalidateQueries(['scavenger-challenges', eventId]);
      setEditingChallenge(null);
    }
  });

  // Delete challenge mutation
  const deleteChallengeMutation = useMutation({
    mutationFn: (challengeId) => api.delete(`/admin/events/${eventId}/scavenger/challenges/${challengeId}`),
    onSuccess: () => {
      toast.success('Challenge deleted');
      queryClient.invalidateQueries(['scavenger-challenges', eventId]);
    }
  });

  // Complete challenge mutation (delegate scans QR)
  const completeChallengeMutation = useMutation({
    mutationFn: (challengeId) => api.post(`/events/${eventId}/scavenger/complete/${challengeId}`),
    onSuccess: (data) => {
      toast.success(`+${data.data.points} points earned!`);
      queryClient.invalidateQueries(['scavenger-progress', eventId]);
      queryClient.invalidateQueries(['scavenger-leaderboard', eventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to complete challenge')
  });

  const totalPoints = challenges.reduce((sum, c) => sum + c.points, 0);
  const earnedPoints = myProgress?.total_points || 0;
  const completedCount = myProgress?.completed_challenges?.length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scavenger-hunt">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-500" />
            Scavenger Hunt
          </h2>
          <p className="text-slate-500">
            {isAdmin ? 'Create challenges for attendees' : 'Explore, scan, and earn points!'}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={isAddingChallenge} onOpenChange={setIsAddingChallenge}>
            <DialogTrigger asChild>
              <Button data-testid="add-challenge-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Challenge</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Challenge title"
                  value={newChallenge.title}
                  onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
                />
                <Textarea
                  placeholder="Description"
                  value={newChallenge.description}
                  onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
                  rows={2}
                />
                <Input
                  placeholder="Location (e.g., 'Near the main stage')"
                  value={newChallenge.location}
                  onChange={(e) => setNewChallenge({ ...newChallenge, location: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Points</label>
                    <Input
                      type="number"
                      value={newChallenge.points}
                      onChange={(e) => setNewChallenge({ ...newChallenge, points: parseInt(e.target.value) || 0 })}
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Hint (optional)</label>
                    <Input
                      placeholder="A helpful clue..."
                      value={newChallenge.hint}
                      onChange={(e) => setNewChallenge({ ...newChallenge, hint: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddingChallenge(false)}>Cancel</Button>
                  <Button 
                    onClick={() => createChallengeMutation.mutate(newChallenge)}
                    disabled={createChallengeMutation.isPending || !newChallenge.title}
                  >
                    Create Challenge
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Progress Bar (Delegate View) */}
      {!isAdmin && (
        <Card className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-purple-200 text-sm">Your Progress</p>
                <p className="text-3xl font-bold">{earnedPoints} / {totalPoints} pts</p>
              </div>
              <div className="text-right">
                <p className="text-purple-200 text-sm">Challenges Completed</p>
                <p className="text-3xl font-bold">{completedCount} / {challenges.length}</p>
              </div>
            </div>
            <Progress 
              value={totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0} 
              className="h-3 bg-purple-400"
            />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="challenges">
        <TabsList>
          <TabsTrigger value="challenges">
            <Target className="h-4 w-4 mr-2" />
            Challenges ({challenges.length})
          </TabsTrigger>
          <TabsTrigger value="leaderboard">
            <Medal className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {challenges.map((challenge, index) => {
              const isCompleted = myProgress?.completed_challenges?.includes(challenge.id);
              
              return (
                <Card 
                  key={challenge.id} 
                  className={`${isCompleted ? 'bg-green-50 border-green-200' : ''} transition-all hover:shadow-md`}
                  data-testid={`challenge-${challenge.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {isCompleted ? <Star className="h-4 w-4" /> : index + 1}
                        </div>
                        <CardTitle className="text-base">{challenge.title}</CardTitle>
                      </div>
                      <Badge variant={isCompleted ? 'default' : 'secondary'} className="ml-2">
                        {challenge.points} pts
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-3">{challenge.description}</p>
                    
                    <div className="flex items-center gap-1 text-sm text-slate-500 mb-3">
                      <MapPin className="h-4 w-4" />
                      {challenge.location}
                    </div>
                    
                    {challenge.hint && !isCompleted && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-3">
                        💡 Hint: {challenge.hint}
                      </p>
                    )}
                    
                    {isAdmin ? (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => setEditingChallenge(challenge)}
                        >
                          <Edit2 className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => deleteChallengeMutation.mutate(challenge.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      isCompleted ? (
                        <Badge className="w-full justify-center bg-green-500">
                          <Star className="h-3 w-3 mr-1" /> Completed!
                        </Badge>
                      ) : (
                        <Button 
                          className="w-full" 
                          variant="outline"
                          onClick={() => completeChallengeMutation.mutate(challenge.id)}
                          disabled={completeChallengeMutation.isPending}
                        >
                          <QrCode className="h-4 w-4 mr-2" />
                          Scan to Complete
                        </Button>
                      )
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            {challenges.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center">
                  <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-600">No Challenges Yet</h3>
                  <p className="text-sm text-slate-500 mt-2">
                    {isAdmin ? 'Create your first challenge to get started!' : 'Check back soon for challenges!'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Top Hunters
              </CardTitle>
              <CardDescription>Attendees with the most points</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.slice(0, 10).map((entry, index) => (
                  <div 
                    key={entry.delegate_id}
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      index < 3 ? 'bg-gradient-to-r from-yellow-50 to-amber-50' : 'bg-slate-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-slate-300 text-slate-700' :
                      index === 2 ? 'bg-amber-600 text-amber-100' :
                      'bg-slate-200 text-slate-600'
                    }`}>
                      {index === 0 ? <Crown className="h-5 w-5" /> : index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{entry.name}</p>
                      <p className="text-sm text-slate-500">{entry.completed_count} challenges</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-purple-600">{entry.total_points}</p>
                      <p className="text-xs text-slate-500">points</p>
                    </div>
                  </div>
                ))}
                
                {leaderboard.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No participants yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Challenge Dialog */}
      {editingChallenge && (
        <Dialog open={!!editingChallenge} onOpenChange={() => setEditingChallenge(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Challenge</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Input
                placeholder="Challenge title"
                value={editingChallenge.title}
                onChange={(e) => setEditingChallenge({ ...editingChallenge, title: e.target.value })}
              />
              <Textarea
                placeholder="Description"
                value={editingChallenge.description || ''}
                onChange={(e) => setEditingChallenge({ ...editingChallenge, description: e.target.value })}
              />
              <Input
                placeholder="Location"
                value={editingChallenge.location || ''}
                onChange={(e) => setEditingChallenge({ ...editingChallenge, location: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  value={editingChallenge.points}
                  onChange={(e) => setEditingChallenge({ ...editingChallenge, points: parseInt(e.target.value) || 0 })}
                />
                <Input
                  placeholder="Hint"
                  value={editingChallenge.hint || ''}
                  onChange={(e) => setEditingChallenge({ ...editingChallenge, hint: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingChallenge(null)}>Cancel</Button>
                <Button onClick={() => updateChallengeMutation.mutate({
                  challengeId: editingChallenge.id,
                  updates: editingChallenge
                })}>
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ScavengerHunt;
